// Track current parent component during tree creation
let currentParentComponent = null;

// Track which component classes have already injected styles
const _injectedStyles = new Set();

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Create a real DOM node from a vnode (element only)
 * @param {Object|string|number} vnode - Virtual node or primitive
 * @param {boolean} isSvg - Whether we're inside an SVG context
 * @returns {Node} DOM node
 */
export function createElement(vnode, isSvg = false) {
    // Text nodes
    if (typeof vnode === 'string' || typeof vnode === 'number') {
        return document.createTextNode(String(vnode));
    }

    // Component vnodes are handled by createComponentNode
    if (typeof vnode.type === 'function') {
        return createComponentNode(vnode);
    }

    // Determine SVG context
    isSvg = isSvg || vnode.type === 'svg';
    // foreignObject children exit SVG namespace
    const childSvg = isSvg && vnode.type !== 'foreignObject';

    // DOM element
    const el = isSvg
        ? document.createElementNS(SVG_NS, vnode.type)
        : document.createElement(vnode.type);
    patchProps(el, {}, vnode.props, isSvg);

    vnode.children.forEach(child => {
        el.appendChild(createElement(child, childSvg));
    });

    if (typeof vnode.props.ref === 'function') {
        vnode.props.ref(el);
    }

    return el;
}

/**
 * Create DOM node for component vnode
 */
function createComponentNode(vnode) {
    const ComponentClass = vnode.type;
    const instance = new ComponentClass(vnode.props);

    vnode._instance = instance;
    instance._parentVnode = vnode;

    // Set parent component from the current parent context
    instance._parent = currentParentComponent;

    instance.willMount?.();

    // Inject component styles once per class
    const Ctor = instance.constructor;
    if (Ctor.styles && !_injectedStyles.has(Ctor)) {
        const style = document.createElement('style');
        style.setAttribute('data-component', Ctor.name);
        style.textContent = Ctor.styles;
        document.head.appendChild(style);
        _injectedStyles.add(Ctor);
    }

    // Set this instance as parent for any child components
    const previousParent = currentParentComponent;
    currentParentComponent = instance;

    const childVnode = instance.render();
    instance._vnode = childVnode;

    const node = createElement(childVnode);
    instance._el = node;

    // Restore previous parent context
    currentParentComponent = previousParent;

    if (typeof vnode.props.ref === 'function') {
        vnode.props.ref(instance);
    }

    instance.didMount?.();

    return node;
}

/**
 * Patch props on a DOM element
 */
export function patchProps(el, oldProps, newProps, isSvg = false) {
    const allProps = new Set([...Object.keys(oldProps), ...Object.keys(newProps)]);

    for (const key of allProps) {
        const oldVal = oldProps[key];
        const newVal = newProps[key];

        if (oldVal === newVal) continue;
        if (key === 'key' || key === 'ref') continue;

        if (key.startsWith('on')) {
            const eventName = key.slice(2).toLowerCase();
            if (oldVal) el.removeEventListener(eventName, oldVal);
            if (newVal) el.addEventListener(eventName, newVal);
        }
        else if (key === 'className') {
            if (isSvg) {
                if (newVal == null) el.removeAttribute('class');
                else el.setAttribute('class', newVal);
            } else {
                el.className = newVal ?? '';
            }
        }
        else if (key === 'style' && typeof newVal === 'object') {
            Object.assign(el.style, newVal);
        }
        else if (!isSvg && key in el && typeof newVal !== 'object') {
            if (newVal == null) {
                el.removeAttribute(key);
            } else {
                el[key] = newVal;
            }
        }
        else {
            if (newVal == null || newVal === false) {
                el.removeAttribute(key);
            } else {
                el.setAttribute(key, newVal === true ? '' : newVal);
            }
        }
    }
}

/**
 * Unmount a vnode tree
 */
export function unmount(vnode) {
    if (vnode == null || typeof vnode === 'string' || typeof vnode === 'number') {
        return;
    }

    if (typeof vnode.props?.ref === 'function') {
        vnode.props.ref(null);
    }

    if (typeof vnode.type === 'function') {
        const instance = vnode._instance;
        if (instance) {
            instance.willUnmount?.();
            instance._executeCleanup?.();
            unmount(instance._vnode);
        }
    } else {
        vnode.children?.forEach(child => unmount(child));
    }
}

/**
 * Update component with new props
 */
function updateComponent(oldVnode, newVnode, parent, index) {
    const instance = oldVnode._instance;
    const oldProps = instance.props;
    const newProps = newVnode.props;

    // Transfer instance to new vnode
    newVnode._instance = instance;
    instance._parentVnode = newVnode;
    instance.props = newProps;

    // Handle ref change
    if (oldVnode.props?.ref !== newVnode.props?.ref) {
        if (typeof oldVnode.props?.ref === 'function') {
            oldVnode.props.ref(null);
        }
        if (typeof newVnode.props?.ref === 'function') {
            newVnode.props.ref(instance);
        }
    }

    // Check if update needed
    if (!instance.shouldUpdate(oldProps, newProps, instance.state, instance.state)) {
        return;
    }

    instance.willUpdate?.();

    // Set this instance as parent for any new child components created during render
    const previousParent = currentParentComponent;
    currentParentComponent = instance;

    const oldChildVnode = instance._vnode;
    const newChildVnode = instance.render();

    diff(parent, oldChildVnode, newChildVnode, index);

    // Restore previous parent context
    currentParentComponent = previousParent;

    instance._vnode = newChildVnode;
    instance._el = parent.childNodes[index];

    instance.didUpdate?.();
}

/**
 * Diff and patch the DOM
 */
export function diff(parent, oldVNode, newVNode, index = 0, isSvg = false) {
    const existingNode = parent.childNodes[index];

    // New node added
    if (oldVNode == null) {
        const newNode = createElement(newVNode, isSvg);
        if (existingNode) {
            parent.insertBefore(newNode, existingNode);
        } else {
            parent.appendChild(newNode);
        }
        return;
    }

    // Node removed
    if (newVNode == null) {
        unmount(oldVNode);
        if (existingNode) {
            existingNode.remove();
        }
        return;
    }

    // Text node changes
    if (typeof oldVNode !== 'object' || typeof newVNode !== 'object') {
        if (oldVNode !== newVNode) {
            parent.replaceChild(createElement(newVNode, isSvg), existingNode);
        }
        return;
    }

    // Different types = replace entirely
    if (oldVNode.type !== newVNode.type) {
        unmount(oldVNode);
        parent.replaceChild(createElement(newVNode, isSvg), existingNode);
        return;
    }

    // Same component type - update
    if (typeof newVNode.type === 'function') {
        updateComponent(oldVNode, newVNode, parent, index);
        return;
    }

    // Determine SVG context for children
    isSvg = isSvg || newVNode.type === 'svg';
    const childSvg = isSvg && newVNode.type !== 'foreignObject';

    // Same element type - update in place
    patchProps(existingNode, oldVNode.props, newVNode.props, isSvg);
    diffChildren(existingNode, oldVNode.children, newVNode.children, childSvg);
}

/**
 * Diff children arrays with keyed reconciliation
 */
export function diffChildren(parentEl, oldChildren, newChildren, isSvg = false) {
    const oldKeyedMap = new Map();
    const oldUnkeyed = [];

    oldChildren.forEach((child, i) => {
        if (child?.key != null) {
            oldKeyedMap.set(child.key, { vnode: child, node: parentEl.childNodes[i], index: i });
        } else {
            oldUnkeyed.push({ vnode: child, node: parentEl.childNodes[i], index: i });
        }
    });

    const newKeysUsed = new Set();
    let unkeyedIndex = 0;

    newChildren.forEach((newChild, i) => {
        let oldEntry = null;

        if (newChild?.key != null) {
            oldEntry = oldKeyedMap.get(newChild.key);
            newKeysUsed.add(newChild.key);
        } else {
            if (unkeyedIndex < oldUnkeyed.length) {
                oldEntry = oldUnkeyed[unkeyedIndex];
                unkeyedIndex++;
            }
        }

        const currentNode = parentEl.childNodes[i];

        if (oldEntry) {
            if (oldEntry.node !== currentNode) {
                parentEl.insertBefore(oldEntry.node, currentNode);
            }
            diff(parentEl, oldEntry.vnode, newChild, i, isSvg);
        } else {
            const newNode = createElement(newChild, isSvg);
            if (currentNode) {
                parentEl.insertBefore(newNode, currentNode);
            } else {
                parentEl.appendChild(newNode);
            }
        }
    });

    oldKeyedMap.forEach((entry, key) => {
        if (!newKeysUsed.has(key) && entry.node.parentNode) {
            unmount(entry.vnode);
            entry.node.remove();
        }
    });

    for (let i = unkeyedIndex; i < oldUnkeyed.length; i++) {
        if (oldUnkeyed[i].node.parentNode) {
            unmount(oldUnkeyed[i].vnode);
            oldUnkeyed[i].node.remove();
        }
    }

    while (parentEl.childNodes.length > newChildren.length) {
        parentEl.lastChild.remove();
    }
}
